import { httpsCallable } from "firebase/functions";
import { functions } from "../firebaseConfig";

/**
 * Result shape returned by the `getTenantDocumentFile` Cloud Function.
 */
export interface TenantDocumentFile {
  base64: string;
  contentType: string;
  fileName: string;
}

interface GetTenantDocumentFileRequest {
  docType: "invoice" | "offer";
  docId: string;
  fileKind: "pdf" | "xml";
}

/**
 * Downloads an invoice/offer PDF or XML file for a cross-tenant advisor context via the
 * tenant-aware `getTenantDocumentFile` Cloud Function (single source of truth).
 *
 * Used only when the loaded document belongs to a foreign scope
 * (`effectiveScopeUid !== currentUser.uid`) — the owner path keeps using direct Storage /
 * PHP-proxy downloads. Callable errors (e.g. `functions/permission-denied`,
 * `functions/resource-exhausted`) are re-thrown for the caller to map to UI messages.
 *
 * @param docType Which collection the document lives in.
 * @param docId Firestore document id.
 * @param fileKind File type to fetch (`offer` supports only `pdf`).
 * @returns The file bytes (base64), its content type and a suggested file name.
 * @throws The underlying Firebase `FunctionsError` (with a `functions/*` code) on failure.
 */
export const downloadTenantDocumentFile = async (
  docType: "invoice" | "offer",
  docId: string,
  fileKind: "pdf" | "xml"
): Promise<TenantDocumentFile> => {
  const callable = httpsCallable<
    GetTenantDocumentFileRequest,
    TenantDocumentFile
  >(functions, "getTenantDocumentFile");

  const result = await callable({ docType, docId, fileKind });
  return result.data;
};

/**
 * Converts a base64 string into a `Uint8Array` (browser-safe, no Node Buffer).
 *
 * @param base64 Base64-encoded file bytes.
 * @returns The decoded bytes.
 */
export const base64ToUint8Array = (base64: string): Uint8Array => {
  const binary = window.atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
};

/**
 * Triggers a browser download of a `TenantDocumentFile` via an object-URL `<a download>`
 * click (mirrors the existing XML blob branch in `InvoiceArchive`).
 *
 * @param file The callable result (base64 + contentType + fileName).
 * @param overrideFileName Optional file name to use instead of the callable default.
 */
export const triggerTenantFileDownload = (
  file: TenantDocumentFile,
  overrideFileName?: string
): void => {
  const bytes = base64ToUint8Array(file.base64);
  const blob = new Blob([bytes], { type: file.contentType });
  const blobUrl = window.URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = overrideFileName || file.fileName;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
};
